/**
 * Cookie Helper Functions
 * Provides utilities for managing HTTP cookies
 */

/**
 * Cookie Names Enum
 * Centralized cookie names to avoid typos and ensure consistency
 */
export const CookieNames = {
  AUTH_TOKEN: 'authToken',
  SESSION_ID: 'sessionId',
  COMPANY_ID: 'companyId',
  REFRESH_TOKEN: 'refreshToken',
  EMAIL: 'email',
  USER_ID: 'userId',
  LAST_LOGIN_TIME: 'lastLoginTime',
};

/**
 * Cookie Helper Functions
 * Provides utilities for managing HTTP cookies
 */

/**
 * Set a cookie with the given name, value, and options
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {Object} options - Cookie options
 * @param {number} options.expires - Expiration time in days (default: 7)
 * @param {string} options.path - Cookie path (default: '/')
 * @param {string} options.domain - Cookie domain (default: current domain)
 * @param {boolean} options.secure - Secure flag (default: false)
 * @param {string} options.sameSite - SameSite attribute ('Strict', 'Lax', 'None') (default: 'Lax')
 * @returns {void}
 */
export const setCookie = (name, value, options = {}) => {
  const {
    expires = 7, // Default to 7 days
    path = '/',
    domain = '',
    secure = false,
    sameSite = 'Lax',
  } = options;

  // Calculate expiration date
  const expirationDate = new Date();
  expirationDate.setTime(expirationDate.getTime() + expires * 24 * 60 * 60 * 1000);

  // Build cookie string
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  // Add expiration
  cookieString += `; expires=${expirationDate.toUTCString()}`;

  // Add path
  cookieString += `; path=${path}`;

  // Add domain if provided
  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  // Add secure flag
  if (secure) {
    cookieString += '; secure';
  }

  // Add SameSite attribute
  if (sameSite) {
    cookieString += `; SameSite=${sameSite}`;
  }

  // Set the cookie
  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
  const nameEQ = `${encodeURIComponent(name)}=`;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }
  return null;
};

/**
 * Delete a cookie by name
 * @param {string} name - Cookie name
 * @param {Object} options - Cookie options (path and domain must match original cookie)
 * @param {string} options.path - Cookie path (default: '/')
 * @param {string} options.domain - Cookie domain (default: '')
 * @returns {void}
 */
export const deleteCookie = (name, options = {}) => {
  const {
    path = '/',
    domain = '',
  } = options;

  // Set cookie with expiration date in the past
  let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC`;

  // Add path
  cookieString += `; path=${path}`;

  // Add domain if provided
  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  // Set the cookie to delete it
  document.cookie = cookieString;
};

/**
 * Check if a cookie exists
 * @param {string} name - Cookie name
 * @returns {boolean} True if cookie exists, false otherwise
 */
export const hasCookie = (name) => {
  return getCookie(name) !== null;
};

/**
 * Get all cookies as an object
 * @returns {Object} Object with cookie names as keys and values as values
 */
export const getAllCookies = () => {
  const cookies = {};
  const cookieArray = document.cookie.split(';');

  cookieArray.forEach((cookie) => {
    const trimmedCookie = cookie.trim();
    const [name, value] = trimmedCookie.split('=');
    if (name && value) {
      cookies[decodeURIComponent(name)] = decodeURIComponent(value);
    }
  });

  return cookies;
};

/**
 * Get all cookie names as an array
 * @returns {Array<string>} Array of cookie names
 */
export const getAllCookieNames = () => {
  const cookieNames = [];
  const cookieArray = document.cookie.split(';');

  cookieArray.forEach((cookie) => {
    const trimmedCookie = cookie.trim();
    const [name] = trimmedCookie.split('=');
    if (name) {
      cookieNames.push(decodeURIComponent(name));
    }
  });

  return cookieNames;
};

/**
 * Delete all cookies
 * Attempts to delete all cookies by trying common path and domain combinations
 * @param {Object} options - Options for deletion
 * @param {Array<string>} options.paths - Array of paths to try (default: ['/', ''])
 * @param {Array<string>} options.domains - Array of domains to try (default: ['', current domain, '.' + current domain])
 * @returns {void}
 */
export const deleteAllCookies = (options = {}) => {
  const {
    paths = ['/', ''],
    domains = ['', window.location.hostname, `.${window.location.hostname}`],
  } = options;

  // Get all cookie names
  const cookieNames = getAllCookieNames();

  // Try to delete each cookie with all path and domain combinations
  cookieNames.forEach((cookieName) => {
    paths.forEach((path) => {
      domains.forEach((domain) => {
        deleteCookie(cookieName, { path, domain });
      });
    });
  });
};
